/**
 * A greeting or voicemail prompt the Company recorded, turned into what
 * a telephone plays (migration 076, ADR-0035).
 *
 * The server keeps one format and converts nothing: 8 kHz, 16-bit, mono
 * WAV, which is what Twilio's <Play> takes as it is. The one way in is
 * the microphone (#410; a file could be handed over too, from #408 until
 * then): what MediaRecorder makes here — webm or ogg, or mp4 on Safari —
 * is decoded by the browser, brought down to that format here, and
 * uploaded. A recording the browser will not decode is refused here with
 * the reason, as one the server will not take is there.
 */
import { barsFrom, BAR_COUNT, type Decoded } from "./waveform";

/** The telephone's rate, and the bounds the server holds a file to. */
const PROMPT_SAMPLE_RATE = 8000;
export const MAX_PROMPT_SECONDS = 60;
const MIN_PROMPT_SECONDS = 1;

/** A recording ready to upload, with what the player draws from it. */
export type TelephoneAudio = Decoded & { wav: Blob };

/** A file or recording refused, carrying the sentence the person reads. */
export class PromptAudioError extends Error {}

/**
 * Decode `audio`, bring it to 8 kHz mono at a level a caller hears, and
 * encode it as the WAV the server takes.
 */
export async function toTelephoneWav(audio: Blob): Promise<TelephoneAudio> {
  const decoded = await decode(audio);
  let samples = downmix(decoded);
  if (decoded.sampleRate !== PROMPT_SAMPLE_RATE) {
    samples = resample(samples, decoded.sampleRate, PROMPT_SAMPLE_RATE);
  }
  const seconds = samples.length / PROMPT_SAMPLE_RATE;
  if (seconds > MAX_PROMPT_SECONDS) {
    throw new PromptAudioError("The recording must be a minute or shorter.");
  }
  if (seconds < MIN_PROMPT_SECONDS) {
    throw new PromptAudioError("The recording is too short to hear.");
  }
  samples = normalise(samples);
  return {
    wav: new Blob([encodeWav(samples, PROMPT_SAMPLE_RATE)], {
      type: "audio/wav",
    }),
    bars: barsFrom(samples, BAR_COUNT),
    seconds,
  };
}

/**
 * The file's samples. Decoded in a context running at the telephone's
 * rate where the browser allows one, because decoding resamples to the
 * context's rate with the browser's own filter, which is better than
 * anything worth writing here; where it does not, at the rate it gives,
 * and `resample` does the rest.
 */
async function decode(audio: Blob): Promise<AudioBuffer> {
  const Offline =
    typeof OfflineAudioContext === "undefined" ? null : OfflineAudioContext;
  if (!Offline) {
    throw new PromptAudioError(
      "This browser cannot read the recording. Try another browser.",
    );
  }
  let ctx: OfflineAudioContext;
  try {
    ctx = new Offline(1, 1, PROMPT_SAMPLE_RATE);
  } catch {
    ctx = new Offline(1, 1, 44100);
  }
  try {
    return await ctx.decodeAudioData(await audio.arrayBuffer());
  } catch {
    throw new PromptAudioError(
      "The recording could not be read. Try recording it again.",
    );
  }
}

/** One channel, the average of all of them. */
export function downmix(buffer: {
  numberOfChannels: number;
  length: number;
  getChannelData(channel: number): Float32Array;
}): Float32Array {
  if (buffer.numberOfChannels === 1) return buffer.getChannelData(0).slice();
  const out = new Float32Array(buffer.length);
  for (let c = 0; c < buffer.numberOfChannels; c++) {
    const data = buffer.getChannelData(c);
    for (let i = 0; i < out.length; i++) out[i] += data[i];
  }
  for (let i = 0; i < out.length; i++) out[i] /= buffer.numberOfChannels;
  return out;
}

/**
 * `samples` at `to` rather than `from`. Each output sample is the
 * average of the input it covers, which filters out what the lower rate
 * cannot hold rather than folding it back as noise — enough for a voice
 * on a telephone, and only reached where the browser would not decode
 * at 8 kHz itself.
 */
export function resample(
  samples: Float32Array,
  from: number,
  to: number,
): Float32Array {
  if (from === to) return samples.slice();
  const ratio = from / to;
  const out = new Float32Array(Math.floor(samples.length / ratio));
  for (let i = 0; i < out.length; i++) {
    const start = i * ratio;
    const end = Math.min(samples.length, start + Math.max(1, ratio));
    let sum = 0;
    let n = 0;
    for (let j = Math.floor(start); j < end; j++) {
      sum += samples[j];
      n++;
    }
    out[i] = n > 0 ? sum / n : 0;
  }
  return out;
}

/**
 * Raised so its loudest moment sits just under full scale: a voice memo
 * held at arm's length is otherwise a greeting the caller strains to
 * hear. Never by more than four times, so a recording of a quiet room is
 * not turned into a recording of its hiss; never lowered.
 */
export function normalise(samples: Float32Array): Float32Array {
  let peak = 0;
  for (const s of samples) peak = Math.max(peak, Math.abs(s));
  if (peak === 0) return samples;
  const gain = Math.min(4, 0.9 / peak);
  if (gain <= 1) return samples;
  return samples.map((s) => s * gain);
}

/** 16-bit PCM WAV, one channel, at `rate`. */
export function encodeWav(samples: Float32Array, rate: number): ArrayBuffer {
  const bytes = samples.length * 2;
  const buffer = new ArrayBuffer(44 + bytes);
  const view = new DataView(buffer);
  const text = (at: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(at + i, s.charCodeAt(i));
  };
  text(0, "RIFF");
  view.setUint32(4, 36 + bytes, true);
  text(8, "WAVE");
  text(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, rate, true);
  view.setUint32(28, rate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  text(36, "data");
  view.setUint32(40, bytes, true);
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
  return buffer;
}

/**
 * Whether this browser can record from the microphone at all. Not where
 * there is no MediaRecorder or no getUserMedia — an older WebView, or a
 * page not served over HTTPS — and then the screen offers the typed words
 * alone rather than a button that fails.
 */
export function canRecordHere(): boolean {
  return (
    typeof MediaRecorder !== "undefined" &&
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices?.getUserMedia === "function"
  );
}

/** A recording being made, and how to finish it. */
export type MicRecording = {
  /** Ends the recording and hands back what was said. */
  stop: () => Promise<Blob>;
  /** Ends it and keeps nothing. */
  cancel: () => void;
  /**
   * How loud the microphone is right now, 0 to 1, for the moving waveform
   * that says it is hearing them. Absent where the browser has no
   * AudioContext to measure with; the recording itself does not need one.
   */
  level?: () => number;
};

/**
 * The microphone's loudness, read off an analyser on the same stream the
 * recorder takes: the RMS of the last few milliseconds, scaled so ordinary
 * speech fills most of the range. Null where there is no AudioContext.
 */
function meter(stream: MediaStream): { level: () => number; close: () => void } | null {
  const Ctx =
    typeof window === "undefined"
      ? undefined
      : (window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext);
  if (!Ctx) return null;
  try {
    const ctx = new Ctx();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    return {
      level: () => {
        analyser.getFloatTimeDomainData(buf);
        let sum = 0;
        for (const v of buf) sum += v * v;
        // Speech sits around 0.02 to 0.2 RMS. A room's own hum, under
        // 0.01, is nothing; above it the level climbs gently, so talking
        // moves the bars and a breath or a keyboard barely does.
        const rms = Math.sqrt(sum / buf.length);
        return Math.min(1, Math.max(0, rms - 0.01) * 4) ** 0.75;
      },
      close: () => void ctx.close().catch(() => {}),
    };
  } catch {
    return null;
  }
}

/**
 * Start recording from the microphone. Rejects with the reason where the
 * person refused the microphone or there is none.
 */
export async function recordFromMic(): Promise<MicRecording> {
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch (err) {
    const name = err instanceof DOMException ? err.name : "";
    throw new PromptAudioError(
      name === "NotAllowedError" || name === "SecurityError"
        ? "Allow the microphone in your browser to record here."
        : "No microphone was found on this device.",
    );
  }
  const recorder = new MediaRecorder(stream);
  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => {
    if (e.data.size > 0) chunks.push(e.data);
  };
  const metered = meter(stream);
  const release = () => {
    metered?.close();
    stream.getTracks().forEach((t) => t.stop());
  };
  recorder.start();
  return {
    level: metered?.level,
    stop: () =>
      new Promise<Blob>((resolve) => {
        recorder.onstop = () => {
          release();
          resolve(new Blob(chunks, { type: recorder.mimeType || "audio/webm" }));
        };
        if (recorder.state === "inactive") recorder.onstop(new Event("stop"));
        else recorder.stop();
      }),
    cancel: () => {
      recorder.onstop = release;
      if (recorder.state === "inactive") release();
      else recorder.stop();
    },
  };
}
